import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Typography, TypographyProps, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMore, BugReport } from '@mui/icons-material';
import { Citation } from '../types/citation';
import { useConfig } from '../hooks/useConfig';
import { extractTextFromStructured, isStructuredResponse } from '../utils/structuredResponseParser';

interface FormattedTextProps {
  content: string;
  variant?: TypographyProps['variant'];
  sx?: TypographyProps['sx'];
  isStreaming?: boolean;
  isUserMessage?: boolean; // New prop to identify user messages
  onCitationsExtracted?: (citations: Citation[]) => void;
}

// Enhanced styled components for user messages (plain text only)
const UserMessageText = styled('div')(() => ({
  '& p': {
    margin: '0.3em 0',
    lineHeight: 1.5,
    '&:first-of-type': {
      marginTop: 0,
    },
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  // Enhanced emoji rendering
  '& .emoji': {
    fontSize: '1.1em',
    verticalAlign: 'middle',
    margin: '0 0.1em',
  },
  // Remove markdown formatting - display as plain text
  // No special styling for *, **, `, #, etc. - just display as-is
  '& strong, & em, & code, & pre': {
    fontWeight: 'inherit',
    fontStyle: 'inherit',
    backgroundColor: 'transparent',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit',
  },
  // Remove quote styling - display as plain text
  '& blockquote': {
    border: 'none',
    paddingLeft: 0,
    margin: '0.3em 0',
    fontStyle: 'inherit',
    opacity: 1,
  },
  // Remove link styling - display as plain text
  '& a': {
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      color: 'inherit',
      textDecoration: 'none',
    },
  },
  // Remove list styling - display as plain text
  '& ul, & ol': {
    listStyle: 'none',
    margin: '0.3em 0',
    paddingLeft: 0,
  },
  '& li': {
    margin: '0.1em 0',
    lineHeight: 1.4,
  },
  // Remove header styling - display as plain text
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    margin: '0.3em 0',
    color: 'inherit',
  },
}));

const StyledMarkdown = styled('div')(({ theme }) => ({
  '& p': {
    margin: {
      xs: '0.3em 0', // Mobile: Tighter paragraph spacing
      md: '0.5em 0'  // Desktop: Standard spacing
    },
    lineHeight: 1.6,
    wordBreak: 'break-word', // Prevent overflow on mobile
    hyphens: 'auto', // Enable hyphenation on mobile
    '&:first-of-type': {
      marginTop: 0,
    },
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '1em 0 0.5em 0',
    fontWeight: 600,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  '& h1': {
    fontSize: '1.5em',
    color: theme.palette.primary.main,
  },
  '& h2': {
    fontSize: '1.3em',
    color: theme.palette.primary.main,
  },
  '& h3': {
    fontSize: '1.1em',
    color: theme.palette.primary.main,
  },
  '& ul, & ol': {
    margin: '0.5em 0',
    paddingLeft: {
      xs: '0.5em', // Mobile: Minimal indentation for maximum width utilization
      sm: '0.8em', // Tablet: Reduced indentation  
      md: '1.5em'  // Desktop: Keep current indentation
    },
  },
  '& li': {
    margin: {
      xs: '0.1em 0',  // Mobile: Tighter spacing
      sm: '0.15em 0', // Tablet: Moderate spacing
      md: '0.25em 0'  // Desktop: Keep current spacing
    },
    lineHeight: {
      xs: 1.6, // Mobile: Better readability on small screens
      md: 1.5  // Desktop: Keep current line height
    },
    paddingLeft: {
      xs: '0.15em', // Mobile: Minimal padding for maximum width utilization
      md: '0'       // Desktop: No extra padding
    },
    wordBreak: 'break-word', // Prevent overflow on long words
  },
  // Nested list optimization for mobile
  '& ul ul, & ol ol, & ul ol, & ol ul': {
    paddingLeft: {
      xs: '0.25em', // Mobile: Ultra-minimal nested indentation for maximum space efficiency
      sm: '0.5em',  // Tablet: Reduced nested indentation
      md: '1em'     // Desktop: Standard nested indentation
    },
    marginTop: '0.05em', // Tighter vertical spacing
    marginBottom: '0.05em',
  },
  // Deeply nested lists (3+ levels) for extreme space optimization
  '& ul ul ul, & ol ol ol, & ul ol ul, & ol ul ol': {
    paddingLeft: {
      xs: '0.15em', // Mobile: Minimal indentation for deep nesting
      sm: '0.4em',  // Tablet: Reduced deep nesting indentation
      md: '0.8em'   // Desktop: Moderate deep nesting indentation
    },
    marginTop: '0.02em',
    marginBottom: '0.02em',
  },
  // Custom bullet styling for mobile
  '& ul li::marker': {
    fontSize: {
      xs: '0.9em', // Mobile: Slightly smaller bullets
      md: '1em'    // Desktop: Standard bullet size
    },
    color: theme.palette.text.secondary,
  },
  '& code': {
    backgroundColor: theme.palette.action.hover,
    padding: '0.1em 0.3em',
    borderRadius: '3px',
    fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
    fontSize: '0.9em',
  },  '& pre': {
    backgroundColor: theme.palette.action.hover,
    padding: '1em',
    borderRadius: '6px',
    overflow: 'auto',
    margin: '0.5em 0',
    fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
    fontSize: '0.9em',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: '1em',
    margin: '0.5em 0',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  '& strong': {
    fontWeight: 600,
  },
  '& em': {
    fontStyle: 'italic',
  },  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    wordBreak: {
      xs: 'break-all', // Mobile: Break long URLs
      md: 'normal'     // Desktop: Normal word breaking
    },
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: '1em 0',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '0.5em 0',
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '0.5em',
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: theme.palette.action.hover,
    fontWeight: 600,
  },
}));

const BlinkingCursor = styled('span')(() => ({
  animation: 'blink 1s infinite',
  marginLeft: '2px',
  '@keyframes blink': {
    '0%, 50%': { opacity: 1 },
    '51%, 100%': { opacity: 0 }
  }
}));

// Helpers for spacing and HTML detection
const hasHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s);
const normalizeBlankLines = (s: string) => {
  // Normalize CRLF to LF, trim edges, and collapse 3+ newlines to a single blank line
  const normalized = s.replace(/\r\n/g, '\n').trim();
  return normalized.replace(/\n{3,}/g, '\n\n');
};

const FormattedText: React.FC<FormattedTextProps> = ({ content, variant = 'body1', sx, isStreaming = false, isUserMessage = false, onCitationsExtracted }) => {
  // Get debug configuration from backend
  const { config } = useConfig();
  
  /**
   * Extract text from structured response during streaming
   * This handles partial JSON during streaming to avoid showing raw JSON to users
   */
  const extractTextDuringStreaming = (text: string): string => {
    // If not streaming or user message, use standard extraction
    if (!isStreaming || isUserMessage) {
      if (!isUserMessage && isStructuredResponse(text)) {
        return extractTextFromStructured(text);
      }
      return text;
    }
    
    // During streaming, look for the "Text" field and extract it progressively
    // Pattern: "Text": "..." or "Text":"..."
    const textFieldMatch = text.match(/"Text"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    
    if (textFieldMatch) {
      // Found the Text field, return its content (unescaping JSON escapes)
      const extractedText = textFieldMatch[1]
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
      return extractedText;
    }
    
    // Check if we're starting to see JSON structure (opening brace)
    // If so, hide it until we can extract the Text field
    if (text.trim().startsWith('{')) {
      // Check if "Text" field has started appearing
      const partialTextMatch = text.match(/"Text"\s*:\s*"/);
      if (partialTextMatch) {
        // We can see the start of the Text field but not the full value yet
        // Return empty string to avoid showing incomplete JSON
        return '';
      }
      // JSON structure starting but no Text field yet - hide it
      return '';
    }
    
    // Not a structured response, return as-is
    return text;
  };
  
  // FIRST: Extract text from structured response (handles both streaming and complete)
  const baseText = React.useMemo(() => {
    return extractTextDuringStreaming(content);
  }, [content, isStreaming, isUserMessage]);
  
    // Enhanced text processing for user messages
  const enhanceUserMessageText = (text: string): string => {
    if (!isUserMessage) return text;

    return text
      // Convert common emoticons to emoji (but leave markdown syntax alone)
      .replace(/:\)/g, '😊')
      .replace(/:\(/g, '😞')
      .replace(/:D/g, '😃')
      .replace(/;D/g, '😉')
      .replace(/:P/g, '😛')
      .replace(/<3/g, '❤️')
      .replace(/\^_\^/g, '😄')
      .replace(/-_-/g, '😑')
      .replace(/o_O/g, '😳')
      .replace(/O_o/g, '😲')
      // Enhance emphasis patterns (but avoid markdown syntax)
      // Only enhance standalone words, not those that are part of markdown
      .replace(/\b(wow|amazing|awesome|incredible|fantastic)\b(?![*_`])/gi, '✨ $1 ✨')
      .replace(/\b(thanks?|thank you|thx)\b(?![*_`])/gi, '🙏 $1')
      .replace(/\b(hello|hi|hey)\b(?![*_`])/gi, '👋 $1')
      .replace(/\b(yes|yep|yeah)\b(?![*_`])/gi, '✅ $1')
      .replace(/\b(no|nope)\b(?![*_`])/gi, '❌ $1')
      // Wrap emojis in span for better styling
      .replace(/([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}])/gu, '<span class="emoji">$1</span>');
  };
  
  // Function to omit incomplete citations during streaming
  const omitIncompleteCitations = (text: string): string => {
    if (!isStreaming) {
      return text;
    }

    // Look for incomplete JSON citations at the end of the text during streaming
    // Pattern: [{'url': '...', 'title': '...' (incomplete)
    let processedText = text;
    let lastIndex = processedText.length;

    // Search backwards for incomplete citation patterns
    for (let i = processedText.length - 1; i >= 0; i--) {
      const char = processedText[i];
      
      // If we find a complete JSON array ending with ], we can stop
      if (char === ']') {
        // Check if this is actually a complete JSON citation
        const beforeBracket = processedText.substring(0, i + 1);
        const citationPattern = /\[\s*\{[^}]*'url'[^}]*\}(?:\s*,\s*\{[^}]*'url'[^}]*\})*\s*\]$/;
        if (citationPattern.test(beforeBracket)) {
          // This is a complete citation, no need to truncate
          break;
        }
      } 
      // If we find an opening bracket that starts a JSON citation
      else if (char === '[') {
        // Look ahead to see if this looks like a JSON citation start
        const afterBracket = processedText.substring(i);
        // Check for JSON citation patterns: [{'url': or [{"url":
        if (/^\[\s*\{['"]url['"]/.test(afterBracket)) {
          // This looks like the start of an incomplete JSON citation
          lastIndex = i;
          break;
        }
      }
    }

    // If we found an incomplete citation, truncate the text
    if (lastIndex < processedText.length) {
      processedText = processedText.substring(0, lastIndex).trim();
    }

    return processedText;
  };

  /**
   * Function to omit incomplete markdown link patterns during streaming
   * Prevents displaying raw markdown like [View Product](URL before the link is complete
   */
  const omitIncompleteMarkdown = (text: string): string => {
    if (!isStreaming) {
      return text;
    }

    let processedText = text;
    
    // Strategy: Look for ]( pattern which indicates a markdown link
    // Then check if the link is complete (has closing ))
    
    // Find all occurrences of ]( from right to left
    let searchIndex = processedText.length;
    
    while (searchIndex > 0) {
      const linkMarkerIndex = processedText.lastIndexOf('](', searchIndex - 1);
      
      if (linkMarkerIndex === -1) {
        // No ]( found, check for incomplete link text patterns
        break;
      }
      
      // Found ](, check if there's a closing ) after it
      const afterLinkMarker = processedText.substring(linkMarkerIndex + 2);
      
      if (!afterLinkMarker.includes(')')) {
        // No closing ), this is an incomplete link
        // Find the opening [ for this link
        let openBracketIndex = -1;
        let bracketDepth = 0;
        
        for (let i = linkMarkerIndex - 1; i >= 0; i--) {
          const char = processedText[i];
          if (char === ']') {
            bracketDepth++;
          } else if (char === '[') {
            if (bracketDepth === 0) {
              openBracketIndex = i;
              break;
            }
            bracketDepth--;
          }
        }
        
        if (openBracketIndex !== -1) {
          // Found the opening [, truncate from there
          return processedText.substring(0, openBracketIndex).trim();
        }
      }
      
      // This link is complete or we couldn't find its start, continue searching
      searchIndex = linkMarkerIndex;
    }
    
    // No ]( pattern found, check for incomplete link text
    // Look for unmatched [ that might be starting a link
    let lastOpenBracket = -1;
    let bracketDepth = 0;
    
    for (let i = processedText.length - 1; i >= 0; i--) {
      const char = processedText[i];
      
      if (char === ']') {
        bracketDepth++;
      } else if (char === '[') {
        if (bracketDepth === 0) {
          // This [ is not matched with a ]
          lastOpenBracket = i;
          break;
        }
        bracketDepth--;
      }
    }
    
    if (lastOpenBracket !== -1) {
      // Found an unmatched [
      const afterBracket = processedText.substring(lastOpenBracket);
      
      // Only truncate if there's text after [ (could be incomplete link text)
      if (afterBracket.length > 1 && !afterBracket.includes(']')) {
        return processedText.substring(0, lastOpenBracket).trim();
      }
    }
    
    return processedText;
  };
  
  // Function to process citations and format them as numbered links
  const processCitations = (text: string): { cleanText: string; citations: Citation[] } => {
    // First, omit incomplete citations if streaming
    const textWithoutIncomplete = omitIncompleteCitations(text);
      // Updated pattern to match both single citations and arrays of citations
    // Matches citations with either single or double quotes for keys
    // Matches: [{'url': '...', 'title': '...', 'content': '...'}] or [{"url": "...", "title": "...", "content": "..."}]
    const citationPattern = /\[\s*\{[^}]*(?:'url'|"url")[^}]*\}(?:\s*,\s*\{[^}]*(?:'url'|"url")[^}]*\})*\s*\]/g;
    const citations: Citation[] = [];
    // Extract all citation JSON arrays from the processed text
    const matches = textWithoutIncomplete.match(citationPattern);
    if (matches) {
      matches.forEach(match => {        try {
          // Custom parser for citations that handles mixed quotes robustly
          const extractedCitations: Citation[] = [];
            // Try different patterns for different quote combinations
          // Updated patterns to handle escaped quotes properly
          const patterns = [
            // Pattern 1: Single quotes for keys, single quotes for url/title, double quotes for content
            // {'url': '...', 'title': '...', 'content': "..."}
            // Uses (?:\\.|[^'\\])* to match escaped quotes or non-quote characters
            /\{\s*'url'\s*:\s*'((?:\\.|[^'\\])*)'\s*,\s*'title'\s*:\s*'((?:\\.|[^'\\])*)'\s*,\s*'content'\s*:\s*"((?:\\.|[^"\\])*)"\s*\}/g,
            
            // Pattern 2: Double quotes throughout
            // {"url": "...", "title": "...", "content": "..."}
            /\{\s*"url"\s*:\s*"((?:\\.|[^"\\])*)"\s*,\s*"title"\s*:\s*"((?:\\.|[^"\\])*)"\s*,\s*"content"\s*:\s*"((?:\\.|[^"\\])*)"\s*\}/g,
            
            // Pattern 3: Single quotes throughout (less common but possible)
            // {'url': '...', 'title': '...', 'content': '...'}
            /\{\s*'url'\s*:\s*'((?:\\.|[^'\\])*)'\s*,\s*'title'\s*:\s*'((?:\\.|[^'\\])*)'\s*,\s*'content'\s*:\s*'((?:\\.|[^'\\])*)'\s*\}/g,
            
            // Pattern 4: Mixed - double quotes for keys, single quotes for values
            // {"url": '...', "title": '...', "content": '...'}
            /\{\s*"url"\s*:\s*'((?:\\.|[^'\\])*)'\s*,\s*"title"\s*:\s*'((?:\\.|[^'\\])*)'\s*,\s*"content"\s*:\s*'((?:\\.|[^'\\])*)'\s*\}/g
          ];
            // Try each pattern until we find matches
          for (const pattern of patterns) {
            pattern.lastIndex = 0; // Reset regex state
            let citationMatch;
            while ((citationMatch = pattern.exec(match)) !== null) {
              const [, url, title, content] = citationMatch;
              // Decode escaped quotes and other escape sequences
              const decodeEscapedString = (str: string) => {
                return str
                  .replace(/\\'/g, "'")  // Unescape single quotes
                  .replace(/\\"/g, '"')  // Unescape double quotes
                  .replace(/\\\\/g, '\\'); // Unescape backslashes
              };
              
              extractedCitations.push({
                url: decodeEscapedString(url),
                title: decodeEscapedString(title),
                content: decodeEscapedString(content)
              });
            }
            // If we found citations with this pattern, we're done
            if (extractedCitations.length > 0) {
              break;
            }
          }
          
          // Add each citation if it's not already included
          extractedCitations.forEach(citation => {
            if (citation.url && !citations.some(c => c.url === citation.url)) {
              citations.push({
                url: citation.url,
                title: citation.title || '',
                content: citation.content || ''
              });
            }
          });
        } catch (error) {
          console.warn('Failed to parse citation JSON:', match, error);
          // Fallback: try to extract URLs manually if JSON parsing fails
          const urlMatches = match.match(/'url':\s*'([^']+)'/g);
          if (urlMatches) {
            urlMatches.forEach(urlMatch => {
              const url = urlMatch.match(/'url':\s*'([^']+)'/)?.[1];
              if (url && !citations.some(c => c.url === url)) {
                citations.push({
                  url: url,
                  title: 'Source',
                  content: ''
                });
              }
            });
          }
        }
      });
    }
    
    // Remove JSON citations from the processed text (without incomplete citations)
    const cleanText = textWithoutIncomplete.replace(citationPattern, '').trim();
    
    // Extract Markdown link citations: [link text](url)
    // This allows citation-based filtering to work with embedded Markdown links
    const markdownLinkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    let markdownMatch;
    
    // Note: We scan the original textWithoutIncomplete, not cleanText, 
    // to catch all markdown links before JSON citations were removed
    markdownLinkPattern.lastIndex = 0; // Reset regex state
    while ((markdownMatch = markdownLinkPattern.exec(textWithoutIncomplete)) !== null) {
      const [, linkText, url] = markdownMatch;
      const trimmedUrl = url.trim();
      
      // Only add if URL is valid and not already in citations (from JSON)
      if (trimmedUrl && !citations.some(c => c.url === trimmedUrl)) {
        citations.push({
          url: trimmedUrl,
          title: linkText.trim() || 'Product Link',
          content: '' // Markdown links don't have content field
        });
      }
    }
    
    return { cleanText, citations };
  };
  // Process citations from baseText (which has structured JSON extracted already)
  const { cleanText, citations } = processCitations(baseText);
  
  // Remove incomplete markdown patterns during streaming
  const textWithoutIncompleteMarkdown = omitIncompleteMarkdown(cleanText);
  
  // Apply text enhancements for user messages
  const enhancedText = enhanceUserMessageText(textWithoutIncompleteMarkdown);
  
  // Notify parent component about extracted citations (both streaming and complete)
  React.useEffect(() => {
    if (citations.length > 0 && onCitationsExtracted) {
      onCitationsExtracted(citations);
    }
  }, [isStreaming, citations, onCitationsExtracted]);
  
  // Use enhancedText directly for display (structured response already extracted in baseText)
  const displayText = enhancedText;
    
  // Check if content contains markdown formatting
  const hasMarkdownFormatting = (text: string): boolean => {
    // For user messages, always treat as plain text (no markdown rendering)
    if (isUserMessage) {
      return false;
    }
    
    const markdownPatterns = [
      /^#{1,6}\s/m, // Headers
      /\*\*.*?\*\*/g, // Bold
      /\*.*?\*/g, // Italic
      /`.*?`/g, // Inline code
      /^```[\s\S]*?```$/m, // Code blocks
      /^[-*+]\s/m, // Unordered lists
      /^\d+\.\s/m, // Ordered lists
      /^>\s/m, // Blockquotes
      /\[.*?\]\(.*?\)/g, // Links
      /^\|.*?\|/m, // Tables - detect lines starting and ending with |
      /^[-|:\s]+$/m, // Table separator lines (like |----|----|)
      /^---+$/m, // Horizontal rules
    ];
      return markdownPatterns.some(pattern => pattern.test(text));
  };
  
  // Use displayText (with structured response extracted) for markdown detection
  const hasMarkdown = hasMarkdownFormatting(displayText);  
  
  // Debug section component
  const renderDebugSection = () => {
    if (!config?.debug || !content.trim()) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="debug-content"
            id="debug-header"
            sx={{ 
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.2)' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BugReport sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
              <Typography variant="subtitle2" sx={{ color: 'warning.main', fontWeight: 600 }}>
                Debug: Raw Streamed Text {isStreaming && '(Streaming...)'}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: 'grey.50', maxHeight: '300px', overflow: 'auto' }}>
            <Typography 
              variant="body2" 
              component="pre"
              sx={{ 
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                padding: 1,
                backgroundColor: 'grey.100',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              {content}
              {isStreaming && <BlinkingCursor>▋</BlinkingCursor>}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };
  // If content doesn't have markdown formatting, render as plain text with line breaks
  if (!hasMarkdown) {
    // Build and normalize text (assistant only) and avoid injecting <br> into existing HTML
    const base = isUserMessage ? enhancedText : textWithoutIncompleteMarkdown;
    const normalized = !isUserMessage ? normalizeBlankLines(base) : base;
    const containsHtml = !isUserMessage && hasHtml(normalized);
    const textToRender = containsHtml ? normalized : normalized.split('\n').join('<br />');
    
    return (
      <Box>
        {isUserMessage ? (
          <UserMessageText sx={{ ...sx }}>
            <div dangerouslySetInnerHTML={{ __html: textToRender }} />
            {isStreaming && <BlinkingCursor>▋</BlinkingCursor>}
          </UserMessageText>
        ) : (
          // For assistant messages, allow HTML rendering for tables and other HTML content
          <Typography variant={variant} sx={{ ...sx, '& br + br': { display: 'none' } }}>
            <div dangerouslySetInnerHTML={{ __html: textToRender }} />
            {isStreaming && <BlinkingCursor>▋</BlinkingCursor>}
          </Typography>
        )}
        {renderDebugSection()}
      </Box>
    );
  }
  // Render markdown content
  const markdownBase = isUserMessage ? enhancedText : displayText;
  const markdownText = markdownBase
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  return (
    <Box>
      {isUserMessage ? (
        <UserMessageText sx={{ ...sx }}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children, ...props }) => (
                <a 
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  {...props}
                >
                  {children}
                </a>
              )
            }}
          >
            {markdownText}
          </ReactMarkdown>
          {isStreaming && <BlinkingCursor>▋</BlinkingCursor>}
        </UserMessageText>
      ) : (
        <StyledMarkdown sx={{ ...sx }}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children, ...props }) => (
                <a 
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  {...props}
                >
                  {children}
                </a>
              )
            }}
          >
            {markdownText}
          </ReactMarkdown>
          {isStreaming && <BlinkingCursor>▋</BlinkingCursor>}
        </StyledMarkdown>
      )}
      {renderDebugSection()}
    </Box>
  );
};

export default FormattedText;
